import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Card, CardHeader, CardBody, Button, Row, Col, Spinner } from "reactstrap";
import { RiArrowUpSLine } from "react-icons/ri";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import { useContext } from "react";
import ProductContext from "../../../../contexts/ProductContext";
import DateUtils from "src/services/utils/DateUtils";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const Confirmation = ({
    t,
    onBackButtonClicked,
    onCancelButtonClicked,
    goToTab
}) => {
    const {
        productData,
        handleSave,
        submitIsInProcess
    } = useContext(ProductContext);

    const dateUtils = new DateUtils();
    const commodity = (productData.name?.commodity || '').toLowerCase();

    const hasEnitityFeature = isFeatureEnabledForActiveModule('entity_management');

    return (
        <div className="">
            {/* Step 1: Product Information */}
            <Card className="mb-3 shadow-sm">
                <CardHeader
                    className="d-flex justify-content-between align-items-center bg-white"
                >
                    <h5 className="mb-0">
                        {t('Product Information')}
                    </h5>
                    <RiArrowUpSLine />
                </CardHeader>

                <CardBody>
                    <Row>
                        <Col sm="12" md="6">
                            <p><strong>{t('NACE Code')}:</strong><br/>
                            {productData.name.label}</p>
                        </Col>
                        <Col sm="12" md="6">
                            <p><strong>{t('Product Name')}:</strong><br/>
                            {productData.productName}</p>
                        </Col>
                        {hasEnitityFeature && (
                            <Col sm="12" md="6">
                                <p><strong>{t('Legal Entities')}:</strong><br />
                                    {productData.legal_entity_ids?.map((l) => l.label).join(', ')}</p>
                            </Col>
                        )}
                        <Col sm="12" md="6">
                            <p>
                                <strong>{t('Lot Number')}:</strong><br/>
                                {productData.lotNumber}
                            </p>
                        </Col>
                        <Col sm="12" md="6">
                            <p>
                                <strong>{t('Internal Product ID')}:</strong><br/> 
                                {productData.internalProductId}
                            </p>
                        </Col>
                        <Col sm="12" md="12">
                            <p>
                                <strong>{t('Description')}:</strong><br/>
                                {productData.description}
                            </p>
                        </Col>
                        <Col sm="12">
                            <Button color="link" onClick={() => {goToTab('information')}}>
                                {t('Edit')}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Step 2: Trade & Customs Details */}
            <Card className="mb-3 shadow-sm">
                <CardHeader
                    className="d-flex justify-content-between align-items-center bg-white"
                >
                    <h5 className="mb-0">
                        {t('Trade & Customs Details')}
                    </h5>
                    <RiArrowUpSLine />
                </CardHeader>

                <CardBody>
                    <Row>
                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Date of Import')}:</strong><br />
                                {dateUtils.convertDateToDate(
                                    productData.importDate,
                                    INTERNATIONAL_DATE_FORMAT
                                )}
                            </p>
                        </Col>

                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Import Country')}:</strong><br />
                                {productData.importFromCountry?.label}
                            </p>
                        </Col>
                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Date of Export')}:</strong><br />
                                {dateUtils.convertDateToDate(
                                    productData.exportDate,
                                    INTERNATIONAL_DATE_FORMAT
                                )}
                            </p>
                        </Col>
                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Export Country')}:</strong> <br />
                                {productData.exportToCountry?.label}
                            </p>
                        </Col>

                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Container Number')}:</strong><br />
                                {productData.containerNumber}
                            </p>
                        </Col>
                        <Col sm='12' md='6'>
                            <p>
                                <strong>{t('Shipment Number')}:</strong> <br />
                                {productData.shipmentNumber}
                            </p>
                        </Col>

                        <Col sm='12' md='3'>
                            <p>
                                <strong>{t('Quantity (Metric Tonnes)')}:</strong><br />
                                {productData.metricTonnes}
                            </p>
                        </Col>
                        <Col sm='12' md='3'>
                            <p>
                                <strong>{t('HS Code (6-digit)')}:</strong><br />
                                {productData.hsCode}
                            </p>
                        </Col>
                        <Col sm='12' md='3'>
                            <p><strong>{t('KN Code (8-digit)')}:</strong><br />{productData.knCode}</p>
                        </Col>
                        <Col sm='12' md='3'>
                            <p><strong>{t('TARIC Code (10-digit)')}:</strong><br />{productData.taricCode}</p>
                        </Col>

                        <Col sm='12' md='12'>
                            <Button color="link" onClick={() => { goToTab('custom-details') }}>
                                {t('Edit')}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>


            {/* Step 3: Due Diligence & Traceability */}
            {commodity && (
                <Card className="mb-3 shadow-sm">
                    <CardHeader
                        className="d-flex justify-content-between align-items-center bg-white"
                    >
                        <h5 className="mb-0">
                            {t('Due Diligence & Traceability')}
                        </h5>
                        <RiArrowUpSLine />
                    </CardHeader>

                    <CardBody>
                        <Row>
                            <Col sm='12'>
                                <p>
                                    <strong>{t('Country of Origin')}:</strong>
                                    <br />
                                    {productData.originCountry.label}
                                </p>
                            </Col>

                            <Col sm='12' md='6'>
                                <p>
                                    <strong>{t('DDS Reference Number')}:</strong>
                                    <br />
                                    {productData.ddsReferenceNumber}
                                </p>
                            </Col>
                            <Col sm='12' md='6'>
                                <p>
                                    <strong>{t('DDS Verification Number')}:</strong><br />
                                    {productData.ddsVerificationNumber}
                                </p>
                            </Col>
                            <Col sm='12'>
                                <Button color="link" onClick={() => { goToTab('traceablility') }}>
                                    {t('Edit')}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            )}

            {/* Step 4: Geolocation Data */}
            {commodity && (
                <Card className="mb-3 shadow-sm">
                    <CardHeader
                        className="d-flex justify-content-between align-items-center bg-white"
                    >
                        <h5 className="mb-0">
                            {t('Geolocation Data')}
                        </h5>
                        <RiArrowUpSLine />
                    </CardHeader>

                    {commodity !== 'cattle' ? (
                        <CardBody>
                            <div className="mb-4">
                                <p><strong>{t('Geolocation Polygon')}:</strong></p>
                                <div className="border rounded p-2" style={{ minHeight: "150px" }}>
                                    <GeoMap
                                        readOnly={true}
                                        preDefinedGeo={productData.geolocationReference}
                                        multiDraw={true}
                                    />
                                </div>
                                <Button color="link" onClick={() => { goToTab('geolocation') }}>
                                    {t('Edit')}
                                </Button>
                            </div>
                        </CardBody>
                    ) : (
                        <CardBody>
                            <div className="mb-4">
                                <p>
                                    <strong>{t('Breeding Farm Location')}</strong>
                                </p>
                                <div className="border rounded p-2" style={{ minHeight: "150px" }}>
                                    <GeoMap
                                        readOnly={true}
                                        preDefinedGeo={productData.geolocationReference}
                                        multiDraw={true}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <p>
                                    <strong>
                                        {t('Fattening Farm Location')}
                                    </strong>
                                </p>
                                <div className="border rounded p-2" style={{ minHeight: "150px" }}>
                                    <GeoMap
                                        readOnly={true}
                                        preDefinedGeo={productData.farmGeolocationReference}
                                        multiDraw={true}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <p>
                                    <strong>
                                        {t('Slaughterhouse Location')}
                                    </strong>
                                </p>
                                <div className="border rounded p-2" style={{ minHeight: "150px" }}>
                                    <GeoMap
                                        readOnly={true}
                                        preDefinedGeo={productData.slaughterGeolocationReference}
                                        multiDraw={true}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <Button color="link" onClick={() => { goToTab('geolocation') }}>
                                    {t('Edit')}
                                </Button>
                            </div>
                        </CardBody>
                    )}
                </Card>
            )}

            {/* Submit Button */}
            <div  className="d-flex justify-content-between">
                <Button
                    color="secondary"
                    type="button"
                    outline
                    onClick={onCancelButtonClicked}
                >
                    {t('Cancel')}
                </Button>

                <div>
                    <Button
                        color="secondary"
                        onClick={onBackButtonClicked}
                        className="me-2"
                        type="button"
                        disabled={submitIsInProcess}
                    >
                        {t('Back')}
                    </Button>

                    <Button
                        color="primary"
                        type="button"
                        onClick={handleSave}
                        disabled={submitIsInProcess}
                    >
                        {submitIsInProcess && (
                            <Spinner 
                                color="white"
                                className="me-2"
                                size="sm"
                            />
                        )}
                        {t('Confirm & Submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(Confirmation))
)